import { useEffect, useState } from "react";
import PageLayout from "../common/PageLayout";
import { useParams } from "react-router-dom";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { TbListDetails } from "react-icons/tb";
import { AppReviewRepository } from "../repositories/AppReviewRepository";
import ReviewDetails from "./ReviewDetails";
import ReviewAttachedDocuments from "./ReviewAttachedDocuments";
import ReviewStatusesPanel from "./ReviewStatusesPanel";
import ReviewOtherDocuments from "./ReviewOtherDocuments";
import ReviewDecisions from "./ReviewDecisions";
import ReviewRequests from "./ReviewRequests";
import ReviewAssignmentPanel from "./ReviewAssignmentPanel";
import { ErrorMessageResolver } from "../common/ErrorMessageResolver";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@emotion/react";
import { notifyShowSuccessMessage } from "../../store/AlertSlice";
import ConfirmationDialog from "../common/ConfirmationDialog";

export default function ReviewDetailedView() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [appReview, setAppReview] = useState(null);
  const [error, setError] = useState(null);
  const [instArchiveNumber, setInstArchiveNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const auth = useSelector((state) => state.auth);
  const theme = useTheme();
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState();
  const dispatch = useDispatch();

  useEffect(() => {
    if (id) {
      getById();
    }
  }, [id]);

  const getById = () => {
    setLoading(true);
    AppReviewRepository.getById(id)
      .then((res) => {
        setLoading(false);
        setAppReview(res.data);
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
        setError(ErrorMessageResolver.resolve(err));
      });
  };

  const attachedDocuments = appReview?.appEntry?.attachments?.filter(
    (attachment) =>
      [
        "URBAN_PLANING_PROJECT_DWG",
        "URBAN_PLANING_PROJECT_PDF",
        "TRAFFIC_ENGINEERING_PROJECT_DWG",
        "TRAFFIC_ENGINEERING_PROJECT_PDF",
        "PROJECT_REVISION",
      ].includes(attachment.documentType),
  );

  const otherDocuments = appReview?.appEntry?.attachments?.filter(
    (attachment) =>
      ["PROOF_OF_PAYMENT", "POWER_OF_ATTORNEY"].includes(
        attachment.documentType,
      ),
  );

  const assignArchiveNumber = () => {
    console.log("Assigning archive number:", instArchiveNumber);
    setSaving(true);
    AppReviewRepository.assignInstArchiveNumber(id, instArchiveNumber)
      .then(() => {
        setSaving(false);
        getById();
        dispatch(
          notifyShowSuccessMessage(
            "Успешно зачуван архивски број на институција. Предметот е архивиран и чека на носење одлука.",
          ),
        );
      })
      .catch((err) => {
        setSaving(false);
        console.error(err);
        setError(ErrorMessageResolver.resolve(err));
      });
  };

  return (
    <PageLayout
      title={appReview?.appEntry?.trackingNumber}
      Icon={TbListDetails}
      subtitle={"Идентификатор"}
    >
      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="300px"
        >
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100px"
        >
          <Alert severity="error" variant="filled">
            {error}
          </Alert>
          <Divider />
        </Box>
      )}

      {!error && !loading && appReview && (
        <Grid container spacing={2}>
          <ReviewStatusesPanel appReview={appReview} />
          {appReview?.reviewStatus === "PROCESSING" &&
            (auth?.userData?.roles?.includes("ROLE_MVR_SIGNER") ||
              auth?.userData?.roles?.includes("ROLE_JPDP_SIGNER") ||
              auth?.userData?.roles?.includes("ROLE_SUPER_ADMIN")) && (
              <ReviewAssignmentPanel appReview={appReview} onAssigned={getById} />
            )}
          {!appReview?.instArchiveNumber &&
            appReview?.appEntry?.status === "INSTITUTIONS_REVIEW" &&
            (auth?.userData?.roles?.includes("ROLE_MVR_RECEIVER") ||
              auth?.userData?.roles?.includes("ROLE_JPDP_RECEIVER")) && (
              <Grid item xs={12} md={12}>
                <Paper elevation={3} sx={{ padding: 2, mb: 2 }}>
                  <Typography variant="h6" component="div">
                    Архивирање на предмет во институција (
                    {auth?.userData?.roles?.includes("ROLE_MVR_RECEIVER") &&
                      "МВР"}
                    {auth?.userData?.roles?.includes("ROLE_JPDP_RECEIVER") &&
                      "ЈПДП"}
                    )
                  </Typography>
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginTop: "10px",
                    }}
                  >
                    <TextField
                      size="small"
                      label="Архивски број"
                      autoComplete="off"
                      value={instArchiveNumber || ""}
                      onChange={(e) => setInstArchiveNumber(e.target.value)}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={!instArchiveNumber}
                      onClick={() =>
                        setOpenConfirmationDialog({
                          title: "Потврда за архивирање",
                          message: `Дали сте сигурни дека сакате да го архивирате предметот и да му доделите архивски број ${instArchiveNumber}?`,
                          onConfirm: () => {
                            assignArchiveNumber();
                            setOpenConfirmationDialog(null);
                          },
                        })
                      }
                      sx={{
                        px: 3,
                        py: 1,
                        "&.Mui-disabled": {
                          backgroundColor: theme.palette.primary.main,
                          color: "white",
                          cursor: "not-allowed",
                          pointerEvents: "auto",
                          opacity: 1,
                        },
                      }}
                    >
                      <Typography
                        style={{
                          fontFamily: "Inter-Regular",
                          fontSize: "14px",
                          color: "#fff",
                        }}
                      >
                        Зачувај
                      </Typography>
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            )}
          <Grid item xs={12} md={6} style={{ marginTop: "10px" }}>
            <Grid container spacing={0}>
              <ReviewDetails appReview={appReview} appEntry={""} />
              {(auth?.userData?.roles?.includes("ROLE_JPDP_RECEIVER") ||
                auth?.userData?.roles?.includes("ROLE_JPDP_REVIEWER") ||
                auth?.userData?.roles?.includes("ROLE_MVR_REVIEWER") ||
                auth?.userData?.roles?.includes("ROLE_MVR_SIGNER") ||
                auth?.userData?.roles?.includes("ROLE_JPDP_SIGNER") ||
                auth?.userData?.roles?.includes("ROLE_JPDP_ADMINISTRATOR") ||
                auth?.userData?.roles?.includes("ROLE_MVR_ADMINISTRATOR")) && (
                <>
                  <ReviewAttachedDocuments
                    appReview={appReview}
                    attachedDocuments={attachedDocuments}
                  />
                  <ReviewOtherDocuments
                    otherDocuments={otherDocuments}
                    appReview={appReview}
                  />
                </>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={0}>
              <ReviewRequests appReview={appReview} />
              {appReview?.instArchiveNumber &&
                (auth?.userData?.roles?.includes("ROLE_JPDP_RECEIVER") ||
                  auth?.userData?.roles?.includes("ROLE_JPDP_REVIEWER") ||
                  auth?.userData?.roles?.includes("ROLE_MVR_RECEIVER") ||
                  auth?.userData?.roles?.includes("ROLE_MVR_REVIEWER") ||
                  auth?.userData?.roles?.includes("ROLE_MVR_SIGNER") ||
                  auth?.userData?.roles?.includes("ROLE_JPDP_SIGNER") ||
                  auth?.userData?.roles?.includes("ROLE_JPDP_ADMINISTRATOR") ||
                  auth?.userData?.roles?.includes(
                    "ROLE_MVR_ADMINISTRATOR",
                  )) && (
                  <ReviewDecisions
                    appReview={appReview}
                    setAppReview={setAppReview}
                  />
                )}
            </Grid>
          </Grid>
        </Grid>
      )}
      {saving && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={true}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {openConfirmationDialog && (
        <ConfirmationDialog
          open={Boolean(openConfirmationDialog)}
          title={openConfirmationDialog.title}
          message={openConfirmationDialog.message}
          onCancel={() => setOpenConfirmationDialog()}
          onConfirm={openConfirmationDialog.onConfirm}
        />
      )}
    </PageLayout>
  );
}
